import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, Clock, AlertCircle } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { deliveryZones } from '../data/mock';
import { toast } from 'sonner';
import TemporaryNotice from '../components/TemporaryNotice';
import OrderingDisabledModal from '../components/OrderingDisabledModal';
import { useRestaurantStatus } from '../hooks/useRestaurantStatus';

const Checkout = () => {
  const navigate = useNavigate();
  const { cartItems, packagingItems, getAllItems, getCartTotal, getPackagingTotal, getGrandTotal, clearCart } = useCart();
  const { loaded, isOpen, deliveryEnabled, takeawayEnabled, dineinEnabled, statusMessage } = useRestaurantStatus();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');
  const [completedOrder, setCompletedOrder] = useState(null); // Store completed order details
  const [showOrderingModal, setShowOrderingModal] = useState(false);

  // Helper: is a specific delivery mode available?
  const isModeAvailable = (mode) => {
    if (!isOpen) return false;
    if (mode === 'takeaway') return takeawayEnabled;
    if (mode === 'delivery') return deliveryEnabled;
    if (mode === 'dinein') return dineinEnabled;
    return false;
  };

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    deliveryType: 'takeaway',
    deliveryZone: '',
    address: '',
    notes: '',
    paymentMethod: 'cash'
  });

  const [errors, setErrors] = useState({});
  const [deliveryFee, setDeliveryFee] = useState(0);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Update delivery fee when zone changes
    if (name === 'deliveryZone' && value) {
      const zone = deliveryZones.find(z => z.id === value);
      setDeliveryFee(zone ? zone.fee : 0);
    }
    
    // Reset delivery fee if switching from delivery to pickup
    if (name === 'deliveryType' && value !== 'delivery') {
      setDeliveryFee(0);
      setFormData(prev => ({ ...prev, deliveryZone: '', address: '' }));
    }
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'A név megadása kötelező';
    }

    const phoneRegex = /^[+]?[0-9\s-]{9,}$/;
    if (!formData.phone.trim()) {
      newErrors.phone = 'A telefonszám megadása kötelező';
    } else if (!phoneRegex.test(formData.phone)) {
      newErrors.phone = 'Érvénytelen telefonszám formátum';
    }

    if (formData.deliveryType === 'delivery') {
      if (!formData.deliveryZone) {
        newErrors.deliveryZone = 'Válassz kiszállítási területet';
      }
      if (!formData.address.trim()) {
        newErrors.address = 'A pontos cím megadása kötelező házhozszállításhoz';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if restaurant is closed
    if (!isOpen) {
      toast.error('Az étterem jelenleg zárva van. Rendelés nem lehetséges.');
      return;
    }

    // Check if selected delivery mode is still available
    if (!isModeAvailable(formData.deliveryType)) {
      toast.error('A kiválasztott átvételi mód jelenleg nem elérhető. Válassz másik módot!');
      return;
    }

    if (!validateForm()) {
      toast.error('Kérjük, töltsd ki a kötelező mezőket!');
      return;
    }

    if (cartItems.length === 0) {
      toast.error('A kosár üres!');
      navigate('/menu');
      return;
    }

    setIsSubmitting(true);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      
      // Calculate all totals
      const cartSubtotal = getCartTotal();
      const packagingTotal = getPackagingTotal();
      const subtotal = cartSubtotal + packagingTotal;
      const finalTotal = subtotal + deliveryFee;
      
      // Prepare order data
      const orderData = {
        customerName: formData.name,
        customerPhone: formData.phone,
        customerEmail: formData.email || null,
        deliveryType: formData.deliveryType,
        deliveryZone: formData.deliveryType === 'delivery' ? formData.deliveryZone : null,
        deliveryAddress: formData.deliveryType === 'delivery' ? formData.address : null,
        deliveryFee: deliveryFee,
        items: getAllItems(), // Include both cart items and packaging
        cartSubtotal: cartSubtotal,
        packagingTotal: packagingTotal,
        subtotal: subtotal,
        total: finalTotal,
        paymentMethod: formData.paymentMethod,
        notes: formData.notes || null
      };

      // Submit order to backend
      const response = await fetch(`${BACKEND_URL}/api/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(orderData)
      });

      if (!response.ok) {
        throw new Error('Hiba a rendelés leadása során');
      }

      const result = await response.json();
      
      // Save email/phone to localStorage for order tracking (Premium UX)
      if (formData.email) {
        localStorage.setItem('lastOrderEmail', formData.email);
      }
      if (formData.phone) {
        localStorage.setItem('lastOrderPhone', formData.phone);
      }
      if (formData.name) {
        localStorage.setItem('lastOrderName', formData.name);
      }
      
      // Store complete order details for confirmation page
      setCompletedOrder({
        orderNumber: result.orderNumber,
        ...formData,
        cartSubtotal: cartSubtotal,
        packagingTotal: packagingTotal,
        deliveryFee: deliveryFee,
        finalTotal: finalTotal,
        items: getAllItems()
      });
      
      setOrderNumber(result.orderNumber);
      setOrderPlaced(true);
      setIsSubmitting(false);
      clearCart();
      toast.success('Rendelés sikeresen leadva!');
      
    } catch (error) {
      console.error('Order submission error:', error);
      setIsSubmitting(false);
      toast.error('Hiba történt a rendelés leadása során. Kérjük, próbáld újra!');
    }
  };

  if (orderPlaced) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-12">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl p-8 sm:p-12 shadow-2xl text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-green-400 to-green-600 rounded-full mb-6">
              <Check className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl font-black text-gray-900 mb-4">
              Rendelésed sikeresen leadva!
            </h1>
            <p className="text-xl text-gray-600 mb-6">
              Rendelésszám: <span className="font-bold text-[#00BCD4]">{orderNumber}</span>
            </p>
            
            <div className="bg-gradient-to-br from-[#00BCD4]/10 to-[#FFA726]/10 rounded-2xl p-6 mb-8">
              <div className="flex items-center justify-center space-x-3 mb-3">
                <Clock className="w-6 h-6 text-[#FFA726]" />
                <p className="text-lg font-bold text-gray-900">
                  Várható elkészítési idő: 20-30 perc
                </p>
              </div>
              <p className="text-gray-600">
                Hamarosan felvesszük veled a kapcsolatot a megadott telefonszámon!
              </p>
            </div>

            <div className="space-y-4 mb-8 text-left bg-gray-50 rounded-xl p-6">
              <h3 className="font-bold text-gray-900 text-lg mb-3">Rendelés részletei:</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Név:</span>
                  <span className="font-semibold text-gray-900">{completedOrder?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Telefon:</span>
                  <span className="font-semibold text-gray-900">{completedOrder?.phone}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Típus:</span>
                  <span className="font-semibold text-gray-900">
                    {completedOrder?.deliveryType === 'takeaway' && 'Elvitel'}
                    {completedOrder?.deliveryType === 'delivery' && 'Házhozszállítás'}
                    {completedOrder?.deliveryType === 'dinein' && 'Helyben fogyasztás'}
                  </span>
                </div>
                {completedOrder?.deliveryType === 'delivery' && completedOrder?.address && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Cím:</span>
                    <span className="font-semibold text-gray-900">{completedOrder.address}</span>
                  </div>
                )}
                <div className="flex justify-between pt-2 border-t border-gray-200">
                  <span className="text-gray-600">Fizetési mód:</span>
                  <span className="font-semibold text-gray-900">
                    {completedOrder?.paymentMethod === 'cash' && 'Készpénz átvételkor'}
                    {completedOrder?.paymentMethod === 'card' && 'Bankkártya átvételkor'}
                    {completedOrder?.paymentMethod === 'szep_otp' && 'OTP SZÉP-kártya átvételkor'}
                  </span>
                </div>
                
                {/* Ordered Items with Notes */}
                {completedOrder?.items && completedOrder.items.length > 0 && (
                  <div className="border-t border-gray-300 pt-3 mt-3">
                    <h4 className="font-bold text-gray-900 mb-2">Rendelt tételek:</h4>
                    <div className="space-y-2">
                      {completedOrder.items.map((item, index) => (
                        <div key={index} className="bg-gray-50 p-3 rounded-lg">
                          <div className="flex justify-between">
                            <div className="flex-1">
                              <p className="font-semibold text-gray-900">
                                {item.quantity}x {item.name}
                              </p>
                              {item.selectedOptions && item.selectedOptions.length > 0 && (
                                <p className="text-xs text-gray-600 mt-1">
                                  + {item.selectedOptions.join(', ')}
                                </p>
                              )}
                              {item.selectedExtras && item.selectedExtras.length > 0 && (
                                <p className="text-xs text-blue-600 mt-1">
                                  Extrák: {item.selectedExtras.join(', ')}
                                </p>
                              )}
                              {item.customerNote && (
                                <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded">
                                  <p className="text-sm text-gray-900">
                                    <span className="font-bold">📝 Megjegyzés:</span> {item.customerNote}
                                  </p>
                                </div>
                              )}
                            </div>
                            <p className="font-bold text-gray-900 ml-3">
                              {item.price * item.quantity} Ft
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Order Summary */}
                <div className="border-t border-gray-300 pt-3 mt-3 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Ételek összesen:</span>
                    <span className="font-semibold text-gray-900">{completedOrder?.cartSubtotal || 0} Ft</span>
                  </div>
                  {completedOrder?.packagingTotal > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Csomagolás:</span>
                      <span className="font-semibold text-blue-600">{completedOrder.packagingTotal} Ft</span>
                    </div>
                  )}
                  {completedOrder?.deliveryFee > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Szállítási díj:</span>
                      <span className="font-semibold text-orange-600">{completedOrder.deliveryFee} Ft</span>
                    </div>
                  )}
                </div>
                
                <div className="flex justify-between pt-3 border-t-2 border-gray-300">
                  <span className="text-lg font-bold text-gray-900">Végösszeg:</span>
                  <span className="text-xl font-black text-[#FFA726]">
                    {completedOrder?.finalTotal || 0} Ft
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <button
                onClick={() => navigate(`/track-order/${orderNumber}`)}
                className="w-full py-4 bg-gradient-to-r from-[#FFA726] to-[#FF9800] text-white rounded-xl font-bold hover:shadow-xl hover:scale-105 transition-all duration-300"
              >
                Rendelés állapotának megtekintése
              </button>
              <button
                onClick={() => navigate('/')}
                className="w-full py-4 bg-gradient-to-r from-[#00BCD4] to-[#00ACC1] text-white rounded-xl font-bold hover:shadow-xl hover:scale-105 transition-all duration-300"
              >
                Vissza a főoldalra
              </button>
              <button
                onClick={() => navigate('/menu')}
                className="w-full py-3 border-2 border-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
              >
                Új rendelés leadása
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (cartItems.length === 0) {
    navigate('/cart');
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-12">
      {/* Ordering Disabled Modal */}
      <OrderingDisabledModal 
        isOpen={showOrderingModal} 
        onClose={() => setShowOrderingModal(false)}
        trigger="checkout"
      />
      
      {/* Temporary Notice */}
      <TemporaryNotice />
      
      {/* Closed Message */}
      {loaded && !isOpen && (
        <div className="bg-gradient-to-r from-red-50 to-rose-50 border-b-4 border-red-500 mb-8">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center space-x-3">
              <AlertCircle className="w-8 h-8 text-red-600 flex-shrink-0" />
              <div>
                <p className="text-lg font-bold text-red-900">{statusMessage}</p>
                <p className="text-red-700">A rendelés leadása jelenleg nem lehetséges.</p>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/cart')}
            className="inline-flex items-center space-x-2 text-[#00BCD4] hover:text-[#00ACC1] font-semibold mb-4 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Vissza a kosárhoz</span>
          </button>
          <h1 className="text-4xl sm:text-5xl font-black text-gray-900">
            Rendelés véglegesítése
          </h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Checkout Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Personal Information */}
              <div className="bg-white rounded-2xl p-6 shadow-md">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Kapcsolati adatok
                </h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Név *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 rounded-xl border-2 ${
                        errors.name ? 'border-red-500' : 'border-gray-200'
                      } focus:border-[#00BCD4] focus:outline-none transition-colors`}
                      placeholder="Teljes név"
                    />
                    {errors.name && (
                      <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Telefonszám *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 rounded-xl border-2 ${
                        errors.phone ? 'border-red-500' : 'border-gray-200'
                      } focus:border-[#00BCD4] focus:outline-none transition-colors`}
                      placeholder="+36 30 123 4567"
                    />
                    {errors.phone && (
                      <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Email (opcionális)
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#00BCD4] focus:outline-none transition-colors"
                      placeholder="pelda@email.com"
                    />
                  </div>
                </div>
              </div>

              {/* Delivery Type */}
              <div className="bg-white rounded-2xl p-6 shadow-md">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Átvétel módja *
                </h2>
                
                <div className="space-y-3">
                  {/* Takeaway */}
                  <label className={`flex items-center p-4 rounded-xl border-2 transition-colors ${
                    !isModeAvailable('takeaway')
                      ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                      : formData.deliveryType === 'takeaway'
                        ? 'border-[#00BCD4] bg-cyan-50 cursor-pointer'
                        : 'border-gray-200 cursor-pointer hover:bg-gray-50'
                  }`}>
                    <input
                      type="radio"
                      name="deliveryType"
                      value="takeaway"
                      checked={formData.deliveryType === 'takeaway'}
                      onChange={handleInputChange}
                      disabled={!isModeAvailable('takeaway')}
                      className="w-5 h-5 text-[#00BCD4]"
                    />
                    <div className="ml-3 flex-1">
                      <p className="font-bold text-gray-900">🥡 Elvitel</p>
                      <p className="text-sm text-gray-600">Személyesen átveszem a helyszínen</p>
                    </div>
                    {!isModeAvailable('takeaway') && (
                      <span className="text-xs font-bold text-red-500 bg-red-50 px-2 py-1 rounded-full">Nem elérhető</span>
                    )}
                  </label>

                  {/* Delivery */}
                  <label className={`flex items-center p-4 rounded-xl border-2 transition-colors ${
                    !isModeAvailable('delivery')
                      ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                      : formData.deliveryType === 'delivery'
                        ? 'border-[#FFA726] bg-orange-50 cursor-pointer'
                        : 'border-gray-200 cursor-pointer hover:bg-gray-50'
                  }`}>
                    <input
                      type="radio"
                      name="deliveryType"
                      value="delivery"
                      checked={formData.deliveryType === 'delivery'}
                      onChange={handleInputChange}
                      disabled={!isModeAvailable('delivery')}
                      className="w-5 h-5 text-[#00BCD4]"
                    />
                    <div className="ml-3 flex-1">
                      <p className="font-bold text-gray-900">🛵 Házhozszállítás</p>
                      <p className="text-sm text-gray-600">Kiszállítjuk a megadott címre</p>
                    </div>
                    {!isModeAvailable('delivery') && (
                      <span className="text-xs font-bold text-red-500 bg-red-50 px-2 py-1 rounded-full">Nem elérhető</span>
                    )}
                  </label>

                  {/* Dine-in */}
                  <label className={`flex items-center p-4 rounded-xl border-2 transition-colors ${
                    !isModeAvailable('dinein')
                      ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                      : formData.deliveryType === 'dinein'
                        ? 'border-purple-400 bg-purple-50 cursor-pointer'
                        : 'border-gray-200 cursor-pointer hover:bg-gray-50'
                  }`}>
                    <input
                      type="radio"
                      name="deliveryType"
                      value="dinein"
                      checked={formData.deliveryType === 'dinein'}
                      onChange={handleInputChange}
                      disabled={!isModeAvailable('dinein')}
                      className="w-5 h-5 text-[#00BCD4]"
                    />
                    <div className="ml-3 flex-1">
                      <p className="font-bold text-gray-900">🍽️ Helyben fogyasztás</p>
                      <p className="text-sm text-gray-600">Az asztalnál fogyasztom el</p>
                    </div>
                    {!isModeAvailable('dinein') && (
                      <span className="text-xs font-bold text-red-500 bg-red-50 px-2 py-1 rounded-full">Nem elérhető</span>
                    )}
                  </label>
                </div>
              </div>

              {/* Delivery Address (shown only if delivery is selected) */}
              {formData.deliveryType === 'delivery' && (
                <div className="bg-white rounded-2xl p-6 shadow-md">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">
                    Szállítási cím
                  </h2>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Válassz kiszállítási területet *
                      </label>
                      <select
                        name="deliveryZone"
                        value={formData.deliveryZone}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 rounded-xl border-2 ${
                          errors.deliveryZone ? 'border-red-500' : 'border-gray-200'
                        } focus:border-[#00BCD4] focus:outline-none transition-colors`}
                      >
                        <option value="">-- Válassz területet --</option>
                        {deliveryZones.map((zone) => (
                          <option key={zone.id} value={zone.id}>
                            {zone.name} ({zone.fee} Ft szállítási díj)
                          </option>
                        ))}
                      </select>
                      {errors.deliveryZone && (
                        <p className="text-red-500 text-sm mt-1">{errors.deliveryZone}</p>
                      )}
                    </div>

                    {formData.deliveryZone && (
                      <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-start space-x-3">
                        <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-semibold text-blue-900">
                            Szállítási díj: {deliveryFee} Ft
                          </p>
                          <p className="text-sm text-blue-700">
                            A kiválasztott terület: {deliveryZones.find(z => z.id === formData.deliveryZone)?.name}
                          </p>
                        </div>
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Pontos cím (utca, házszám, emelet, ajtó) *
                      </label>
                      <input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 rounded-xl border-2 ${
                          errors.address ? 'border-red-500' : 'border-gray-200'
                        } focus:border-[#00BCD4] focus:outline-none transition-colors`}
                        placeholder="pl. Kossuth utca 12, 2. emelet 5."
                      />
                      {errors.address && (
                        <p className="text-red-500 text-sm mt-1">{errors.address}</p>
                      )}
                    </div>

                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
                      <p className="text-sm text-yellow-800">
                        <strong>Fontos:</strong> Kérjük, add meg a pontos címet, beleértve az emeletet és ajtószámot is, hogy a futár megtaláljon!
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Payment Method */}
              <div className="bg-white rounded-2xl p-6 shadow-md">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Fizetési mód *
                </h2>
                
                {/* SZÉP Card Notice */}
                <div className="mb-4 bg-gradient-to-r from-blue-50 to-blue-100 border-2 border-blue-300 rounded-xl p-4">
                  <div className="flex items-center space-x-3">
                    <span className="text-3xl">💳</span>
                    <div>
                      <p className="font-bold text-blue-900">OTP SZÉP-kártya elfogadóhely</p>
                      <p className="text-sm text-blue-700">Fizethet OTP SZÉP-kártyával is!</p>
                    </div>
                  </div>
                </div>
                
                <p className="text-sm text-gray-600 mb-4">
                  A fizetés átvételkor történik. Online fizetési lehetőség nem elérhető.
                </p>
                <div className="space-y-3">
                  <label className="flex items-center p-4 rounded-xl border-2 border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="cash"
                      checked={formData.paymentMethod === 'cash'}
                      onChange={handleInputChange}
                      className="w-5 h-5 text-[#00BCD4]"
                    />
                    <div className="ml-3">
                      <p className="font-bold text-gray-900">Készpénz átvételkor</p>
                      <p className="text-sm text-gray-600">
                        Fizetés {formData.deliveryType === 'delivery' ? 'kiszállításkor' : 'átvételkor'} készpénzzel
                      </p>
                    </div>
                  </label>

                  <label className="flex items-center p-4 rounded-xl border-2 border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="card"
                      checked={formData.paymentMethod === 'card'}
                      onChange={handleInputChange}
                      className="w-5 h-5 text-[#00BCD4]"
                    />
                    <div className="ml-3">
                      <p className="font-bold text-gray-900">Bankkártya átvételkor</p>
                      <p className="text-sm text-gray-600">
                        Fizetés {formData.deliveryType === 'delivery' ? 'kiszállításkor' : 'átvételkor'} bankkártyával
                      </p>
                    </div>
                  </label>

                  <label className="flex items-center p-4 rounded-xl border-2 border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="szep_otp"
                      checked={formData.paymentMethod === 'szep_otp'}
                      onChange={handleInputChange}
                      className="w-5 h-5 text-[#00BCD4]"
                    />
                    <div className="ml-3">
                      <p className="font-bold text-gray-900">OTP SZÉP-kártya átvételkor</p>
                      <p className="text-sm text-gray-600">
                        Fizetés {formData.deliveryType === 'delivery' ? 'kiszállításkor' : 'átvételkor'} OTP SZÉP-kártyával
                      </p>
                    </div>
                  </label>
                </div>
              </div>

              {/* Additional Notes */}
              <div className="bg-white rounded-2xl p-6 shadow-md">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  📝 Megjegyzés a rendeléshez
                </h2>
                <p className="text-sm text-gray-600 mb-4">
                  Itt adhatsz meg bármilyen különleges kérést vagy információt a rendeléssel kapcsolatban.
                </p>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#00BCD4] focus:outline-none transition-colors resize-none"
                  placeholder="Pl.: csengő nem működik, kérem hívjon; extra csípős szósz külön; hagyma nélkül; stb."
                />
              </div>
            </form>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl p-6 shadow-lg sticky top-24">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Rendelésed
              </h2>

              <div className="space-y-3 mb-6 max-h-64 overflow-y-auto">
                {[...cartItems, ...packagingItems].map((item) => (
                  <div key={item.id} className="flex justify-between text-sm">
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900">{item.name}</p>
                      <p className="text-gray-500">{item.quantity}x {item.price} Ft</p>
                      {item.isPackaging && (
                        <span className="text-xs text-blue-600">Csomagolás</span>
                      )}
                    </div>
                    <p className="font-bold text-gray-900">
                      {item.quantity * item.price} Ft
                    </p>
                  </div>
                ))}
              </div>

              <div className="border-t border-gray-200 pt-4 space-y-3 mb-6">
                <div className="flex justify-between text-gray-600">
                  <span>Ételek összesen:</span>
                  <span className="font-semibold">{getCartTotal()} Ft</span>
                </div>
                {getPackagingTotal() > 0 && (
                  <div className="flex justify-between text-gray-600">
                    <span>Csomagolás:</span>
                    <span className="font-semibold text-blue-600">{getPackagingTotal()} Ft</span>
                  </div>
                )}
                {deliveryFee > 0 && (
                  <div className="flex justify-between text-gray-600">
                    <span>Szállítási díj:</span>
                    <span className="font-semibold text-orange-600">{deliveryFee} Ft</span>
                  </div>
                )}
                <div className="border-t border-gray-200 pt-3">
                  <div className="flex justify-between items-center">
                    <span className="text-xl font-bold text-gray-900">Végösszeg:</span>
                    <span className="text-2xl font-black text-[#FFA726]">
                      {getGrandTotal() + deliveryFee} Ft
                    </span>
                  </div>
                </div>
              </div>

              <button
                onClick={handleSubmit}
                disabled={isSubmitting || !isOpen || !isModeAvailable(formData.deliveryType)}
                className={`w-full py-4 rounded-xl font-bold text-lg transition-all duration-300 ${
                  isSubmitting || !isOpen || !isModeAvailable(formData.deliveryType)
                    ? 'bg-gray-400 text-gray-200 cursor-not-allowed opacity-70'
                    : 'bg-gradient-to-r from-[#00BCD4] to-[#00ACC1] text-white hover:shadow-xl hover:scale-105'
                }`}
              >
                {!isOpen
                  ? '🔒 Rendelés jelenleg nem lehetséges (ZÁRVA)'
                  : !isModeAvailable(formData.deliveryType)
                  ? '🔒 Ez az átvételi mód jelenleg nem elérhető'
                  : isSubmitting
                  ? 'Rendelés leadása...'
                  : 'Rendelés leadása'}
              </button>

              <div className="mt-6 p-4 bg-gradient-to-br from-[#00BCD4]/10 to-[#FFA726]/10 rounded-xl">
                <p className="text-xs text-gray-600 text-center">
                  A rendelés leadásával elfogadod az általános szerződési feltételeinket.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
